import type { ReactNode } from "react";
import { DesktopSidebar } from "@/components/DesktopSidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import type { ClientNavTab } from "@/components/MobileNavbar";
import { ClientNavSpacer } from "@/components/client/ClientNavSpacer";

type ClientPageLayoutProps = {
  activeTab: ClientNavTab;
  header?: ReactNode;
  children: ReactNode;
  /** Extra classes on the main content wrapper */
  contentClassName?: string;
  /** When true, skip bottom nav clearance (handled elsewhere, e.g. sticky composer) */
  noNavPadding?: boolean;
  /** When true, content fills remaining viewport height (flex column chain) */
  fillViewport?: boolean;
};

export function ClientPageLayout({
  activeTab,
  header,
  children,
  contentClassName,
  noNavPadding = false,
  fillViewport = false,
}: ClientPageLayoutProps) {
  const isMobile = useIsMobile();
  const showNavSpacer = isMobile && !noNavPadding;

  return (
    <div
      className={cn(
        "gradient-dark",
        fillViewport ? "min-h-dvh flex flex-col" : "min-h-screen"
      )}
    >
      <DesktopSidebar activeTab={activeTab} />

      {header && (
        <div
          className={cn(
            "bg-gradient-to-r from-dark-bg to-dark-card text-white rounded-b-[2.5rem] border-b-2 border-primary/20 flex-shrink-0",
            fillViewport
              ? "p-4 pt-5 pb-4 sm:p-5 sm:pt-6 sm:pb-5"
              : "p-4 pt-6 pb-6 sm:p-6 sm:pt-8 sm:pb-10",
            !isMobile && "md:ml-16"
          )}
        >
          {header}
        </div>
      )}

      <div
        className={cn(
          fillViewport
            ? "flex flex-1 flex-col min-h-0"
            : "space-y-4 sm:space-y-6",
          "px-4 pt-4 sm:px-6 sm:pt-6",
          !showNavSpacer && "pb-4 sm:pb-6",
          !isMobile && "md:ml-16",
          contentClassName
        )}
      >
        {children}
        {showNavSpacer && <ClientNavSpacer />}
      </div>
    </div>
  );
}
